// SPDX-License-Identifier: AGPL-3.0-or-later

import {GroupRemoveUserFailedModal} from '@app/features/app/components/alerts/GroupRemoveUserFailedModal';
import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import Authentication from '@app/features/auth/state/Authentication';
import * as ChannelCommands from '@app/features/channel/commands/ChannelCommands';
import * as PrivateChannelCommands from '@app/features/channel/commands/PrivateChannelCommands';
import {ChangeGroupDMNicknameModal} from '@app/features/channel/components/modals/ChangeGroupDMNicknameModal';
import type {Channel} from '@app/features/channel/models/Channel';
import Channels from '@app/features/channel/state/Channels';
import {GroupOwnershipTransferFailedModal} from '@app/features/guild/components/alerts/GroupOwnershipTransferFailedModal';
import {Logger} from '@app/features/platform/utils/AppLogger';
import Relationships from '@app/features/relationship/state/Relationships';
import {
	ChangeNicknameIcon,
	EditIcon,
	RemoveFromGroupIcon,
	TransferOwnershipIcon,
} from '@app/features/ui/action_menu/ContextMenuIcons';
import {DataMenuRenderer} from '@app/features/ui/action_menu/DataMenuRenderer';
import {StartVoiceCallMenuItem} from '@app/features/ui/action_menu/items/CallMenuItems';
import {CopyUserIdMenuItem} from '@app/features/ui/action_menu/items/CopyMenuItems';
import {DebugUserMenuItem} from '@app/features/ui/action_menu/items/DebugMenuItems';
import {useDMMenuData} from '@app/features/ui/action_menu/items/DMMenuData';
import {MuteDMMenuItem} from '@app/features/ui/action_menu/items/DMMenuItems';
import {InviteToCommunityMenuItem} from '@app/features/ui/action_menu/items/InviteMenuItems';
import {MentionUserMenuItem} from '@app/features/ui/action_menu/items/MentionUserMenuItem';
import {MessageUserMenuItem} from '@app/features/ui/action_menu/items/MessageUserMenuItem';
import {
	BlockUserMenuItem,
	ChangeFriendNicknameMenuItem,
	RelationshipActionMenuItem,
	UnblockUserMenuItem,
} from '@app/features/ui/action_menu/items/RelationshipMenuItems';
import {ReportUserMenuItem} from '@app/features/ui/action_menu/items/ReportUserMenuItem';
import {AddNoteMenuItem} from '@app/features/ui/action_menu/items/UserNoteMenuItems';
import {UserProfileMenuItem} from '@app/features/ui/action_menu/items/UserProfileMenuItem';
import {
	LocalMuteParticipantMenuItem,
	ParticipantVolumeSubmenu,
} from '@app/features/ui/action_menu/items/VoiceParticipantMenuItems';
import {MenuGroup} from '@app/features/ui/action_menu/MenuGroup';
import {MenuItem} from '@app/features/ui/action_menu/MenuItem';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import UserSettings from '@app/features/user/state/UserSettings';
import Users from '@app/features/user/state/Users';
import {hasActiveDirectCallWithUser, isActiveCallParticipant} from '@app/features/voice/utils/PrivateCallMenuUtils';
import {ChannelTypes} from '@app/vendor/constants/ChannelConstants';
import {RelationshipTypes} from '@app/vendor/constants/UserConstants';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useMemo} from 'react';

const logger = new Logger('GroupDMContextMenu');
interface GroupDMContextMenuProps {
	channel: Channel;
	onClose: () => void;
}
export const GroupDMContextMenu: React.FC<GroupDMContextMenuProps> = observer(({channel, onClose}) => {
	const {t} = useLingui();
	const {groups} = useDMMenuData(channel, null, {
		onClose,
	});
	const excludeLabels = useMemo(() => [t`Mute Conversation`, t`Unmute Conversation`], [t]);
	return (
		<>
			<DataMenuRenderer groups={groups} excludeLabels={excludeLabels} />
			<MenuGroup>
				<MuteDMMenuItem channel={channel} onClose={onClose} />
			</MenuGroup>
		</>
	);
});
interface GroupDMMemberContextMenuProps {
	userId: string;
	channelId: string;
	onClose: () => void;
}
export const GroupDMMemberContextMenu: React.FC<GroupDMMemberContextMenuProps> = observer(
	({userId, channelId, onClose}) => {
		const {t} = useLingui();
		const currentUserId = Authentication.currentUserId;
		const channel = Channels.getChannel(channelId);
		const user = Users.getUser(userId);
		const developerMode = UserSettings.developerMode;
		const handleChangeNickname = useCallback(() => {
			if (!user) return;
			onClose();
			ModalCommands.push(modal(() => <ChangeGroupDMNicknameModal channelId={channelId} user={user} />));
		}, [channelId, onClose, user]);
		const handleRemoveFromGroup = useCallback(() => {
			if (!user) return;
			onClose();
			ModalCommands.push(
				modal(() => (
					<ConfirmModal
						title={t`Remove from Group`}
						description={t`Are you sure you want to remove ${user.username} from the group?`}
						primaryText={t`Remove`}
						primaryVariant="danger-primary"
						onPrimary={async () => {
							try {
								await PrivateChannelCommands.removeRecipient(channelId, userId);
								ToastCommands.createToast({
									type: 'success',
									children: t`Removed from group`,
								});
							} catch (error) {
								logger.error('Failed to remove from group:', error);
								window.setTimeout(() => {
									ModalCommands.push(modal(() => <GroupRemoveUserFailedModal username={user.username} />));
								}, 0);
							}
						}}
					/>
				)),
			);
		}, [channelId, onClose, t, user, userId]);
		const handleMakeGroupOwner = useCallback(() => {
			if (!user) return;
			onClose();
			ModalCommands.push(
				modal(() => (
					<ConfirmModal
						title={t`Transfer Ownership`}
						description={t`Are you sure you want to make ${user.username} the group owner? You will lose owner privileges.`}
						primaryText={t`Transfer Ownership`}
						onPrimary={async () => {
							try {
								await ChannelCommands.update(channelId, {owner_id: userId});
								ToastCommands.createToast({
									type: 'success',
									children: t`Ownership transferred`,
								});
							} catch (error) {
								logger.error('Failed to transfer ownership:', error);
								window.setTimeout(() => {
									ModalCommands.push(modal(() => <GroupOwnershipTransferFailedModal username={user.username} />));
								}, 0);
							}
						}}
					/>
				)),
			);
		}, [channelId, onClose, t, user, userId]);
		if (!user || !channel) {
			return null;
		}
		const isGroupDM = channel.type === ChannelTypes.GROUP_DM;
		if (!isGroupDM) {
			return null;
		}
		const isSelf = userId === currentUserId;
		const isOwner = channel.ownerId === currentUserId;
		const relationship = Relationships.getRelationship(userId);
		const relationshipType = relationship?.type;
		const showCallParticipantItems = !isSelf && isActiveCallParticipant(channel, userId);
		const showStartVoiceCall = !user.bot && !hasActiveDirectCallWithUser(userId);
		const advancedMenuGroup = (
			<MenuGroup>
				{developerMode && <DebugUserMenuItem user={user} onClose={onClose} />}
				<CopyUserIdMenuItem user={user} onClose={onClose} />
			</MenuGroup>
		);
		if (isSelf) {
			return (
				<>
					<MenuGroup>
						<UserProfileMenuItem user={user} guildId={undefined} onClose={onClose} />
						<MentionUserMenuItem user={user} onClose={onClose} />
						<MenuItem icon={<EditIcon />} onClick={handleChangeNickname}>
							{t`Change My Group Nickname`}
						</MenuItem>
					</MenuGroup>
					{advancedMenuGroup}
				</>
			);
		}
		return (
			<>
				<MenuGroup>
					<UserProfileMenuItem user={user} guildId={undefined} onClose={onClose} />
					<MentionUserMenuItem user={user} onClose={onClose} />
					<MessageUserMenuItem user={user} onClose={onClose} />
					{showStartVoiceCall && <StartVoiceCallMenuItem user={user} onClose={onClose} />}
					<AddNoteMenuItem user={user} onClose={onClose} />
					<ChangeFriendNicknameMenuItem user={user} onClose={onClose} />
				</MenuGroup>
				{showCallParticipantItems && (
					<MenuGroup>
						<ParticipantVolumeSubmenu userId={user.id} />
					</MenuGroup>
				)}
				{isOwner && (
					<MenuGroup>
						<MenuItem icon={<RemoveFromGroupIcon />} onClick={handleRemoveFromGroup} danger>
							{t`Remove from Group`}
						</MenuItem>
						{!user.bot && (
							<MenuItem icon={<TransferOwnershipIcon />} onClick={handleMakeGroupOwner} danger>
								{t`Make Group Owner`}
							</MenuItem>
						)}
						<MenuItem icon={<ChangeNicknameIcon />} onClick={handleChangeNickname}>
							{t`Change Group Nickname`}
						</MenuItem>
					</MenuGroup>
				)}
				<MenuGroup>
					<InviteToCommunityMenuItem user={user} onClose={onClose} />
					<RelationshipActionMenuItem user={user} onClose={onClose} />
					<ReportUserMenuItem user={user} onClose={onClose} />
					{relationshipType === RelationshipTypes.BLOCKED ? (
						<UnblockUserMenuItem user={user} onClose={onClose} />
					) : (
						<BlockUserMenuItem user={user} onClose={onClose} />
					)}
				</MenuGroup>
				{showCallParticipantItems && (
					<MenuGroup>
						<LocalMuteParticipantMenuItem userId={user.id} onClose={onClose} />
					</MenuGroup>
				)}
				{advancedMenuGroup}
			</>
		);
	},
);
